// src/components/ClientDetailPage.js
import React, { useState, useEffect, useMemo } from "react";
import { db } from "../firebase";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  Grid,
  IconButton,
  Divider,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  Tooltip,
  Stack,
  Alert,
  Snackbar,
  Menu,
  MenuItem,
  ListItemIcon,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BusinessIcon from "@mui/icons-material/Business";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import HistoryIcon from "@mui/icons-material/History";

import { useNavigation } from "../contexts/NavigationContext";
import ClientJobHistory from "./ClientJobHistory";
import CustomerPortalManager from "./CustomerPortalManager";

import { getStatusColor } from "../utils/statusHelpers";
import { formatDate, formatTime } from "../utils/formatHelpers";

function ClientDetailPage({ clientId, onBack }) {
  const { selectJob, navigateToAdminPage } = useNavigation();
  
  const [client, setClient] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [isLoadingClient, setIsLoadingClient] = useState(true);
  const [isLoadingJobs, setIsLoadingJobs] = useState(true);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editedClient, setEditedClient] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  
  // Job menu state
  const [jobMenuAnchor, setJobMenuAnchor] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);

  // Job history dialog state
  const [historyOpen, setHistoryOpen] = useState(false);

  // Fetch client details
  useEffect(() => {
    setIsLoadingClient(true);
    const clientDocRef = doc(db, "customers", clientId);

    const fetchClient = async () => {
      try {
        const docSnap = await getDoc(clientDocRef);
        if (docSnap.exists()) {
          const clientData = { id: docSnap.id, ...docSnap.data() };
          setClient(clientData);
          setEditedClient(clientData);
        } else {
          console.error("No such client document!");
          setClient(null);
        }
      } finally {
        setIsLoadingClient(false);
      }
    };

    fetchClient();
  }, [clientId]);


  // Refetch client data (for portal manager updates)
  const refetchClientData = async () => {
    const clientDocRef = doc(db, "customers", clientId);
    try {
      const docSnap = await getDoc(clientDocRef);
      if (docSnap.exists()) {
        const clientData = { id: docSnap.id, ...docSnap.data() };
        setClient(clientData);
        setEditedClient(clientData);
      }
    } catch (error) {
      console.error("Error refetching client data:", error);
    }
  };
  // Fetch job history
  useEffect(() => {
    if (!client) return;

    setIsLoadingJobs(true);

    const jobsQuery = query(
      collection(db, "jobs"),
      where("customerName", "==", client.name),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(
      jobsQuery,
      (querySnapshot) => {
        const jobsList = [];
        querySnapshot.forEach((doc) => {
          jobsList.push({ id: doc.id, ...doc.data() });
        });
        setJobs(jobsList);
        setIsLoadingJobs(false);
      },
      (err) => {
        console.error("Error fetching jobs for client:", err);
        setIsLoadingJobs(false);
      }
    );

    return () => unsubscribe();
  }, [client]);

  // Calculate statistics
  const stats = useMemo(() => {
    const total = jobs.length;
    const completed = jobs.filter(j => j.status === "Completed").length;
    const active = jobs.filter(j => 
      j.status && !["Completed", "Incomplete"].includes(j.status)
    ).length;
    const completionRate = total > 0 ? ((completed / total) * 100).toFixed(1) : 0;

    return { total, completed, active, completionRate };
  }, [jobs]);

  // Handle edit
  const handleEditOpen = () => {
    setEditedClient(client);
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditedClient(client);
  };

  const handleEditSave = async () => {
    setIsSaving(true);
    try {
      const clientRef = doc(db, "customers", clientId);
      await updateDoc(clientRef, {
        name: editedClient.name,
        contactEmail: editedClient.contactEmail,
        contactPhone: editedClient.contactPhone,
        address: editedClient.address,
      });
      
      setClient(editedClient);
      setEditDialogOpen(false);
      setSnackbar({ open: true, message: "Client updated successfully!", severity: "success" });
    } catch (error) {
      console.error("Error updating client:", error);
      setSnackbar({ open: true, message: "Failed to update client", severity: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle delete
  const handleDeleteOpen = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await deleteDoc(doc(db, "customers", clientId));
      setSnackbar({ open: true, message: "Client deleted successfully!", severity: "success" });
      setTimeout(() => onBack(), 1500);
    } catch (error) {
      console.error("Error deleting client:", error);
      setSnackbar({ open: true, message: "Failed to delete client", severity: "error" });
      setIsDeleting(false);
    }
  };

  // Handle job menu
  const handleJobMenuOpen = (event, job) => {
    setJobMenuAnchor(event.currentTarget);
    setSelectedJob(job);
  };

  const handleJobMenuClose = () => {
    setJobMenuAnchor(null);
    setSelectedJob(null);
  };

  const handleViewJob = () => {
    if (selectedJob) {
      selectJob(selectedJob.id);
    }
    handleJobMenuClose();
  };

  const handleCreateJobForClient = () => {
    navigateToAdminPage('createJob');
    // You could pass client data to pre-fill the form
  };

  // Handle job history dialog
  const handleOpenHistory = () => {
    setHistoryOpen(true);
  };

  const handleCloseHistory = () => {
    setHistoryOpen(false);
  };

  if (isLoadingClient) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 8,
        }}
      >
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (!client) {
    return (
      <Box>
        <Button 
          onClick={onBack} 
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 2 }}
        >
          Back to Clients
        </Button>
        <Alert severity="error">
          Client not found.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Page header */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              bgcolor: "primary.main",
              fontSize: "1.75rem",
              fontWeight: 700,
            }}
          >
            {client.name?.[0]?.toUpperCase() || "C"}
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              {client.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Client since {client.createdAt ? formatDate(client.createdAt) : "N/A"}
            </Typography>
          </Box>
        </Box>
        
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
          >
            Back
          </Button>
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={handleOpenHistory}
            disabled={jobs.length === 0}
          >
            View History ({jobs.length})
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleCreateJobForClient}
          >
            New Job
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEditOpen}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteOpen}
          >
            Delete
          </Button>
        </Stack>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: "linear-gradient(135deg, rgba(37,99,235,0.15), rgba(15,23,42,0.95))",
            border: "1px solid rgba(59,130,246,0.35)",
          }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ bgcolor: "primary.main" }}>
                  <AssignmentIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Jobs
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: "linear-gradient(135deg, rgba(16,185,129,0.18), rgba(15,23,42,0.9))",
            border: "1px solid rgba(34,197,94,0.35)",
          }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ bgcolor: "success.main" }}>
                  <AssignmentIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {stats.completed}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: "linear-gradient(135deg, rgba(56,189,248,0.18), rgba(15,23,42,0.9))",
            border: "1px solid rgba(59,130,246,0.3)",
          }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ bgcolor: "info.main" }}>
                  <CalendarTodayIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {stats.active}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Jobs
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: "linear-gradient(135deg, rgba(139,92,246,0.18), rgba(15,23,42,0.9))",
            border: "1px solid rgba(139,92,246,0.35)",
          }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ bgcolor: "secondary.main" }}>
                  <TrendingUpIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {stats.completionRate}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Success Rate
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Client details */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 3,
              border: "1px solid rgba(148,163,184,0.2)",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Contact Information
            </Typography>

            <Stack spacing={2.5}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Avatar sx={{ bgcolor: "rgba(59,130,246,0.1)" }}>
                  <EmailIcon sx={{ color: "primary.main" }} />
                </Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Email Address
                  </Typography>
                  <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
                    {client.contactEmail || "Not provided"}
                  </Typography>
                </Box>
              </Box>

              <Divider />

              <Box sx={{ display: "flex", gap: 2 }}>
                <Avatar sx={{ bgcolor: "rgba(16,185,129,0.1)" }}>
                  <PhoneIcon sx={{ color: "success.main" }} />
                </Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Phone Number
                  </Typography>
                  <Typography variant="body1">
                    {client.contactPhone || "Not provided"}
                  </Typography>
                </Box>
              </Box>

              <Divider />

              <Box sx={{ display: "flex", gap: 2 }}>
                <Avatar sx={{ bgcolor: "rgba(139,92,246,0.1)" }}>
                  <LocationOnIcon sx={{ color: "secondary.main" }} />
                </Avatar>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Address
                  </Typography>
                  <Typography variant="body1">
                    {client.address || "Not provided"}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Job history */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 3,
              border: "1px solid rgba(148,163,184,0.2)",
              maxHeight: "70vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Job History ({jobs.length})
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleCreateJobForClient}
              >
                Create Job
              </Button>
            </Box>

            {isLoadingJobs ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 6,
                }}
              >
                <CircularProgress size={32} />
              </Box>
            ) : jobs.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <AssignmentIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Jobs Yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Create the first job for this client
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateJobForClient}
                >
                  Create First Job
                </Button>
              </Box>
            ) : (
              <List sx={{ overflow: "auto", flexGrow: 1 }}>
                {jobs.map((job) => {
                  const scheduled = job.scheduledDate || job.scheduledDateTime;
                  const scheduledLabel = formatDate(scheduled);
                  const scheduledTime = formatTime(scheduled);

                  return (
                    <ListItem
                      key={job.id}
                      divider
                      sx={{
                        py: 1.5,
                        px: 2,
                        borderRadius: 2,
                        mb: 0.5,
                        transition: "all 0.2s",
                        "&:hover": {
                          bgcolor: "rgba(56,189,248,0.08)",
                        },
                      }}
                      secondaryAction={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Chip
                            label={job.status || "Unknown"}
                            color={getStatusColor(job.status)}
                            size="small"
                          />
                          <IconButton
                            size="small"
                            onClick={(e) => handleJobMenuOpen(e, job)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemText
                        primary={
                          <Typography sx={{ fontWeight: 600 }}>
                            {job.jobTemplateName || "Job"}{" "}
                            {job.jobReferenceNumber && `#${job.jobReferenceNumber}`}
                          </Typography>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" color="text.secondary">
                              📍 {job.siteAddress || "No address"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              🚗 Vehicle: {job.vehicleReg || "N/A"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              📅 Scheduled: {scheduledLabel}
                              {scheduledTime && ` at ${scheduledTime}`}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Customer Portal Management */}
      <Box sx={{ mt: 3 }}>
        <CustomerPortalManager
          customer={client}
          customerId={clientId}
          onUpdate={refetchClientData}
        />
      </Box>

      {/* Edit Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={handleEditClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Client Details</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Client Name"
              fullWidth
              value={editedClient.name || ""}
              onChange={(e) => setEditedClient({ ...editedClient, name: e.target.value })}
            />
            <TextField
              label="Contact Email"
              fullWidth
              type="email"
              value={editedClient.contactEmail || ""}
              onChange={(e) => setEditedClient({ ...editedClient, contactEmail: e.target.value })}
            />
            <TextField
              label="Contact Phone"
              fullWidth
              value={editedClient.contactPhone || ""}
              onChange={(e) => setEditedClient({ ...editedClient, contactPhone: e.target.value })}
            />
            <TextField
              label="Address"
              fullWidth
              multiline
              rows={3}
              value={editedClient.address || ""}
              onChange={(e) => setEditedClient({ ...editedClient, address: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button 
            onClick={handleEditSave} 
            variant="contained"
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteClose}>
        <DialogTitle>Delete Client?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. The client record will be permanently deleted.
          </Alert>
          <Typography>
            Are you sure you want to delete <strong>{client.name}</strong>?
          </Typography>
          {jobs.length > 0 && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              Warning: This client has {jobs.length} job(s) associated with them.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose}>Cancel</Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete Client"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Job Actions Menu */}
      <Menu
        anchorEl={jobMenuAnchor}
        open={Boolean(jobMenuAnchor)}
        onClose={handleJobMenuClose}
      >
        <MenuItem onClick={handleViewJob}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Job Details</ListItemText>
        </MenuItem>
      </Menu>

      {/* Job History Dialog */}
      <ClientJobHistory
        open={historyOpen}
        onClose={handleCloseHistory}
        client={client}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ClientDetailPage;